import { refreshTransactionDerived, uniqueCodeFromAmount, type Transaction } from "./finance.ts";

export const TRANSACTIONS_KEY = "ek-finance-transactions";
export const BACKUP_KEY = "ek-finance-transactions-before-demo-cleanup-v1";
export const ASSUMPTION_RESET_BACKUP_KEY = "ek-finance-transactions-before-v0120-reset";
export const ASSUMPTION_RESET_MARKER_KEY = "ek-finance-assumption-reset-v0120";

// Match the legacy seed precisely; category may have been edited by the user.
// Imported files and enriched/changed records are retained.
const legacySignatures = new Set<string>([
  "[\"20260916EKR8W3K2\",\"2026-09-16\",1850013,\"CREDIT\",\"SUCCESS\",\"Sedekah harian September\",\"88712001455\",\"7667837267\",\"Data demo\",\"BSI Utama\",\"\"]",
  "[\"20260916PREM4J91\",\"2026-09-16\",3250027,\"CREDIT\",\"SUCCESS\",\"Kelas Premium Batch 09\",\"88712004218\",\"7667837267\",\"Data demo\",\"BSI Utama\",\"\"]",
  "[\"20260915META7Q12\",\"2026-09-15\",1200000,\"DEBIT\",\"SUCCESS\",\"Meta Ads campaign September\",\"7667837267\",\"72748372039\",\"Data demo\",\"BSI Utama\",\"\"]",
  "[\"20260914MEMB2K44\",\"2026-09-14\",975018,\"CREDIT\",\"SUCCESS\",\"Membership tahunan\",\"88712001992\",\"7667837267\",\"Data demo\",\"BSI Utama\",\"\"]",
  "[\"20260913PAYR9T31\",\"2026-09-13\",750000,\"DEBIT\",\"SUCCESS\",\"Kafalah pemateri\",\"7667837267\",\"72748372039\",\"Data demo\",\"BSI Utama\",\"\"]",
  "[\"20260912EXCL5B77\",\"2026-09-12\",2400009,\"CREDIT\",\"SUCCESS\",\"Kelas Eksklusif September\",\"88712006631\",\"7667837267\",\"Data demo\",\"BSI Utama\",\"\"]",
  "[\"20260911CANV6R28\",\"2026-09-11\",219000,\"DEBIT\",\"SUCCESS\",\"Canva subscription\",\"7667837267\",\"72748372039\",\"Data demo\",\"BSI Utama\",\"\"]",
  "[\"20260910UNKN3D15\",\"2026-09-10\",500005,\"CREDIT\",\"SUCCESS\",\"Transfer masuk\",\"88712008712\",\"7667837267\",\"Data demo\",\"BSI Utama\",\"\"]",
  "[\"20260909FAIL1P54\",\"2026-09-09\",420000,\"DEBIT\",\"FAILED\",\"Vendor cetak\",\"7667837267\",\"72748372039\",\"Data demo\",\"BSI Utama\",\"\"]"
]);
const signatureFields = ["paymentRef", "date", "amount", "direction", "status", "subject", "sourceAccount", "beneficiaryAccount", "sourceFile", "accountLabel", "transferType"] as const;

export function removeLegacyDemo(transactions: Transaction[]): Transaction[] {
  return transactions.filter((item) => !legacySignatures.has(
    JSON.stringify(signatureFields.map((key) => item[key])),
  ));
}

type StorageAccess = Pick<Storage, "getItem" | "setItem">;

export function loadTransactions(storage: StorageAccess): Transaction[] {
  const raw = storage.getItem(TRANSACTIONS_KEY);
  if (raw === null) return [];
  const parsed: unknown = JSON.parse(raw);
  const strings = ["id", "paymentRef", "date", "subject", "sourceAccount",
    "beneficiaryAccount", "transferType", "category", "accountLabel",
    "sourceFile", "fingerprint"];
  if (!Array.isArray(parsed) || !parsed.every((row) =>
    row && typeof row === "object" &&
    strings.every((key) => typeof row[key] === "string") &&
    typeof row.amount === "number" && Number.isFinite(row.amount) &&
    ["CREDIT", "DEBIT"].includes(row.direction) &&
    ["", "SUCCESS", "FAILED", "PENDING"].includes(row.status)
  )) throw new Error("INVALID_STORED_TRANSACTIONS");
  const mustResetAssumptions = storage.getItem(ASSUMPTION_RESET_MARKER_KEY) !== "complete";
  if (mustResetAssumptions && storage.getItem(ASSUMPTION_RESET_BACKUP_KEY) === null) {
    storage.setItem(ASSUMPTION_RESET_BACKUP_KEY, raw);
  }
  const upgraded = parsed.map((item) => refreshTransactionDerived({
    ...item,
    uniqueCode: typeof item.uniqueCode === "string"
      ? item.uniqueCode.padStart(3, "0")
      : uniqueCodeFromAmount(item.amount),
    businessUnit: mustResetAssumptions ? "unassigned" : typeof item.businessUnit === "string" ? item.businessUnit : "unassigned",
    businessUnitMappingSource: mustResetAssumptions ? undefined : item.businessUnitMappingSource,
    category: mustResetAssumptions ? "uncategorized" : typeof item.category === "string" ? item.category : "uncategorized",
    categoryAssignmentSource: mustResetAssumptions ? "unassigned" : item.categoryAssignmentSource || "unassigned",
    financialAccount: mustResetAssumptions ? undefined : item.financialAccount,
    financialMappingSource: mustResetAssumptions ? undefined : item.financialMappingSource,
    subject: item.subject === "Tanpa keterangan" ? "" : item.subject,
    accountLabel: item.accountLabel === "BSI CUZ" ? "" : item.accountLabel,
    sourceBatchIds: Array.isArray(item.sourceBatchIds)
      ? item.sourceBatchIds.filter((value: unknown): value is string => typeof value === "string")
      : [],
    sourceFiles: Array.isArray(item.sourceFiles)
      ? item.sourceFiles.filter((value: unknown): value is string => typeof value === "string")
      : [item.sourceFile],
    ...(item.entryMethod === "manual" || item.sourceFile === "Input Manual"
      ? { entryMethod: "manual" as const }
      : {}),
    missingFields: Array.isArray(item.missingFields) ? item.missingFields : [],
    confirmedEmptyFields: Array.isArray(item.confirmedEmptyFields) ? item.confirmedEmptyFields : [],
    dataReviewStatus: item.dataReviewStatus || "complete",
  } as Transaction)) as Transaction[];
  const clean = removeLegacyDemo(upgraded);
  if (clean.length !== parsed.length || JSON.stringify(clean) !== raw) {
    // Retain the exact pre-migration bytes before replacing any stored data.
    if (storage.getItem(BACKUP_KEY) === null) storage.setItem(BACKUP_KEY, raw);
    storage.setItem(TRANSACTIONS_KEY, JSON.stringify(clean));
  }
  if (mustResetAssumptions) storage.setItem(ASSUMPTION_RESET_MARKER_KEY, "complete");
  return clean;
}
